<script setup lang="ts">
import { PhMagnifyingGlass, PhX } from '@phosphor-icons/vue'
import { computed, shallowRef } from 'vue'

import { useHistoryValue } from '@/composables/useHistoryValue'
import type { IndexedStop } from '@/composables/useTransitIndex'
import { matchesNormalized, normalizeForSearch } from '@/utils/textSearch'

const props = withDefaults(
  defineProps<{
    stops: IndexedStop[]
    placeholder?: string
    limit?: number
    /**
     * Opt in to remembering the typed text per history entry, so back returns the
     * field as the user left it. Opt-in rather than automatic because the planner's
     * `EndpointPicker` mounts this too, and its state belongs to a separate,
     * deliberately deferred piece of work.
     */
    restoreId?: string
  }>(),
  { placeholder: 'Search stop point…', limit: 20 },
)

const emit = defineEmits<{ select: [stopId: number] }>()

const query = shallowRef('')

/**
 * Whether the results panel is showing. Deliberately NOT derived from `query`:
 * that would make a restored query reopen the panel over the map the sibling
 * restore just put back, so the user would trade one annoyance for another. Every
 * path that sets a non-empty query by hand also opens the panel, so the visible
 * behaviour for a typing user is unchanged.
 */
const suggestionsOpen = shallowRef(false)

const searchMemory = props.restoreId
  ? useHistoryValue('search-text', props.restoreId, query)
  : undefined

const results = computed<IndexedStop[]>(() => {
  const trimmed = normalizeForSearch(query.value)
  if (!trimmed) return []
  const found: IndexedStop[] = []
  for (const stop of props.stops) {
    // Matching against the precomputed key — normalising 4,991 names per
    // keystroke is the obvious implementation and the wrong one.
    if (matchesNormalized(stop.normalizedName, trimmed)) {
      found.push(stop)
      if (found.length >= props.limit) break
    }
  }
  return found
})

function choose(stopId: number) {
  const typed = query.value
  query.value = ''
  suggestionsOpen.value = false
  /**
   * The clear above is part of leaving this screen, not an edit the user made to
   * it, so what gets remembered for this history entry is what they had typed.
   * Without this, back would land on the cleared field — the very complaint this
   * feature exists to fix, since tapping a suggestion is how people leave.
   */
  searchMemory?.remember(typed)
  emit('select', stopId)
}

function clear() {
  query.value = ''
  suggestionsOpen.value = false
}
</script>

<template>
  <div class="relative">
    <div class="flex items-center gap-2 border border-input bg-background px-3">
      <PhMagnifyingGlass class="size-4 shrink-0 text-muted-foreground" />
      <input
        v-model="query"
        type="search"
        class="min-h-11 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
        :placeholder="placeholder"
        aria-label="Search stop point"
        @focus="suggestionsOpen = true"
        @input="suggestionsOpen = true"
      />
      <button
        v-if="query"
        type="button"
        class="shrink-0 text-muted-foreground"
        aria-label="Clear search"
        @click="clear"
      >
        <PhX class="size-4" />
      </button>
    </div>

    <!--
      Keyed on stop.id, never on name: 4,991 stops share only 3,288 distinct
      names, so two genuinely different stops routinely collide by name and
      keying on it would drop one of them from the list.
    -->
    <ul
      v-if="suggestionsOpen && results.length > 0"
      class="absolute inset-x-0 top-full z-20 max-h-80 overflow-y-auto border border-t-0 border-input bg-background"
    >
      <li v-for="stop in results" :key="stop.id">
        <button
          type="button"
          class="flex min-h-12 w-full flex-col items-start gap-0.5 border-b px-3 py-2 text-left last:border-b-0 hover:bg-muted"
          @click="choose(stop.id)"
        >
          <span class="text-sm">{{ stop.name }}</span>
        </button>
      </li>
    </ul>

    <p
      v-else-if="suggestionsOpen && query.trim()"
      class="absolute inset-x-0 top-full z-20 border border-t-0 border-input bg-background px-3 py-3 text-xs text-muted-foreground"
    >
      No stop matches “{{ query }}”.
    </p>
  </div>
</template>
